import { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import {
    ENTITLED_STRIPE_SUBSCRIPTION_STATUSES,
    RECOVERABLE_STRIPE_SUBSCRIPTION_STATUSES,
    summarizeStripeSubscriptions,
} from '../utils/stripeSubscriptionState';

export interface StripeSubscriptionData {
    renewalDate: number | null;
    cancelAtPeriodEnd: boolean;
    hasPendingUpdate: boolean;
    paymentPastDue: boolean;
    loading: boolean;
}

const EMPTY_SUBSCRIPTION_DATA: StripeSubscriptionData = {
    renewalDate: null,
    cancelAtPeriodEnd: false,
    hasPendingUpdate: false,
    paymentPastDue: false,
    loading: false,
};

export function useStripeSubscription(uid?: string | null): StripeSubscriptionData {
    const [data, setData] = useState<StripeSubscriptionData>({
        renewalDate: null,
        cancelAtPeriodEnd: false,
        hasPendingUpdate: false,
        paymentPastDue: false,
        loading: true,
    });

    useEffect(() => {
        if (!uid) {
            setData(EMPTY_SUBSCRIPTION_DATA);
            return;
        }

        const subscriptionsRef = collection(db, 'customers', uid, 'subscriptions');
        const q = query(
            subscriptionsRef,
            where('status', 'in', [
                ...ENTITLED_STRIPE_SUBSCRIPTION_STATUSES,
                ...RECOVERABLE_STRIPE_SUBSCRIPTION_STATUSES,
            ])
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                if (snapshot.empty) {
                    setData(EMPTY_SUBSCRIPTION_DATA);
                    return;
                }

                setData({
                    ...summarizeStripeSubscriptions(snapshot.docs.map((doc) => doc.data())),
                    loading: false,
                });
            },
            (error) => {
                if (error?.code !== 'permission-denied') {
                    console.error("Error fetching stripe subscriptions:", error);
                }
                setData(EMPTY_SUBSCRIPTION_DATA);
            }
        );

        return () => unsubscribe();
    }, [uid]);

    return data;
}
